// Contact queue view helpers: URL filters, tiers, buckets and row wording. The backend derives
// tier, contact state and queue order; this file only names and formats them.
import { fmtEur } from '../../player/playerFormat';
import { daysAgo, formatShortDay, PAGE_SIZE, todayISO } from './trackerView';

// ACTIVITY_METRICS: which last-activity timestamp measures how quiet a player is.
export const ACTIVITY_METRICS = [
  { key: 'bet', label: 'Bet', field: 'last_bet_at' },
  { key: 'login', label: 'Login', field: 'last_login_at' },
  { key: 'deposit', label: 'Deposit', field: 'last_deposit_at' },
  { key: 'bonus', label: 'Bonus', field: 'last_bonus_activated_at' },
];

// TIERS: days since the last activity of the metric; tiers do not overlap.
export const TIERS = [
  { key: 'all', label: 'All', dot: 'bg-ink-dim' },
  { key: 'active', label: 'Active', pill: 'Active', dot: 'bg-positive', tone: 'positive' },
  { key: 'quiet_7', label: 'Quiet 7–13d', pill: 'Quiet', dot: 'bg-ink-dim', tone: 'neutral' },
  { key: 'quiet_14', label: 'Quiet 14–29d', pill: 'Quiet', dot: 'bg-warning', tone: 'warning' },
  { key: 'inactive_30', label: 'Inactive 30d+', pill: 'Inactive', dot: 'bg-negative', tone: 'negative' },
  { key: 'none', label: 'No activity', pill: 'No activity', dot: 'bg-ink-mute', tone: 'muted' },
];

// BUCKETS double as the list's state filter; "due" (overdue + never contacted) is the default.
export const BUCKETS = [
  { key: 'due', label: 'Due now', dot: 'bg-cta' },
  { key: 'overdue', label: 'Overdue', dot: 'bg-negative' },
  { key: 'never', label: 'Never contacted', dot: 'bg-warning' },
  { key: 'upcoming', label: 'Upcoming 7d', dot: 'bg-brand-blue' },
];

export const SORTS = [
  { key: 'ngr_30d', label: 'NGR 30d' },
  { key: 'ngr_total', label: 'NGR total' },
];

const DEFAULTS = { metric: 'bet', tier: 'all', state: 'due', sort: 'ngr_30d', page: 1 };
const keys = (list) => list.map((x) => x.key);
const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

const positiveInt = (raw) => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};

export const metricInfo = (key) => ACTIVITY_METRICS.find((m) => m.key === key) || ACTIVITY_METRICS[0];
export const tierInfo = (key) => TIERS.find((t) => t.key === key) || TIERS[TIERS.length - 1];

// queueFiltersFromSearch reads the queue filters from the URL; unusable values fall back to the
// defaults. The search box stays out of the URL: it may hold an email.
export const queueFiltersFromSearch = (searchParams) => ({
  metric: pick(searchParams.get('activity'), keys(ACTIVITY_METRICS), DEFAULTS.metric),
  tier: pick(searchParams.get('tier'), keys(TIERS), DEFAULTS.tier),
  state: pick(searchParams.get('state'), keys(BUCKETS), DEFAULTS.state),
  sort: pick(searchParams.get('sort'), keys(SORTS), DEFAULTS.sort),
  page: positiveInt(searchParams.get('page')) || 1,
});

// searchWithQueueFilters writes only the non-default filters, so the plain URL is the due queue.
export const searchWithQueueFilters = (filters) => {
  const out = new URLSearchParams();
  if (filters.metric !== DEFAULTS.metric) out.set('activity', filters.metric);
  if (filters.tier !== DEFAULTS.tier) out.set('tier', filters.tier);
  if (filters.state !== DEFAULTS.state) out.set('state', filters.state);
  if (filters.sort !== DEFAULTS.sort) out.set('sort', filters.sort);
  if (filters.page > 1) out.set('page', String(filters.page));
  return out;
};

export const queueQuery = (filters, search) => {
  const params = {
    activity: filters.metric, state: filters.state, sort: filters.sort, page: filters.page, page_size: PAGE_SIZE,
  };
  if (filters.tier !== 'all') params.tier = filters.tier;
  const q = (search || '').trim();
  if (q) params.search = q;
  return params;
};

// bucketSub: the line under each bucket count.
export const bucketSub = (key, summary, tier) => {
  const s = summary || {};
  switch (key) {
    case 'overdue': return s.overdue ? `oldest ${s.oldest_overdue_days}d late` : 'all on time';
    case 'never': return `of ${tier === 'all' ? s.total : (s.tiers?.[tier] || 0)} VIPs`;
    case 'upcoming': return `${s.contacted_today || 0} contacted today`;
    default: return 'overdue + never contacted';
  }
};

export const bucketCount = (key, summary) => {
  const s = summary || {};
  return { due: s.due_now, overdue: s.overdue, never: s.never, upcoming: s.upcoming_7d }[key] ?? 0;
};

export const tierCount = (key, summary) => (key === 'all' ? summary?.total : summary?.tiers?.[key]) || 0;

export const showingLabel = (filters, total) => {
  const bucket = filters.state === 'upcoming' ? 'Upcoming' : BUCKETS.find((b) => b.key === filters.state).label;
  const tier = filters.tier === 'all' ? '' : ` · ${tierInfo(filters.tier).label}`;
  return `${bucket}${tier} · ${total ?? 0}`;
};

export const coverage = (summary) => {
  const total = summary?.total || 0;
  const covered = summary?.contacted_7d || 0;
  return { covered, total, pct: total ? Math.round((covered / total) * 100) : 0 };
};

const ago = (n) => (n === null ? '—' : n === 0 ? 'today' : `${n}d`);

// activityLine: "last bet 4d ago" for the chosen metric.
export const activityLine = (row, metric, now = new Date()) => {
  const m = metricInfo(metric);
  const n = daysAgo(row.activity?.[m.field], now);
  const what = m.label.toLowerCase();
  if (n === null) return `no ${what} on record`;
  if (n === 0) return `last ${what} today`;
  if (n === 1) return `last ${what} yesterday`;
  return `last ${what} ${n}d ago`;
};

// activityTip: every metric at once ("Bet 4d · Deposit today · …").
export const activityTip = (row, now = new Date()) => ACTIVITY_METRICS
  .map((m) => `${m.label} ${ago(daysAgo(row.activity?.[m.field], now))}`).join(' · ');

export const ngrCell = (row, sort) => (sort === 'ngr_total'
  ? { main: fmtEur(row.ngr_total), sub: `30d ${fmtEur(row.ngr_30d)}` }
  : { main: fmtEur(row.ngr_30d), sub: `total ${fmtEur(row.ngr_total)}` });

const dayLabel = (iso, now) => (daysAgo(iso, now) === 0 ? 'Today' : formatShortDay(iso));

export const lastContactCell = (row, now = new Date()) => {
  const c = row.last_contact;
  return c ? { top: dayLabel(c.contacted_at, now), sub: c.channel } : { top: 'Never', sub: 'no contact yet' };
};

const managerName = (email) => String(email || '').split('@')[0];

export const latestNoteCell = (row, now = new Date()) => {
  const c = row.last_contact;
  if (!c?.note) return null;
  return { text: c.note, meta: [dayLabel(c.contacted_at, now), managerName(c.manager_email)].filter(Boolean).join(' · ') };
};

// daysUntil counts whole UTC calendar days from now to a future day (0 for today or earlier).
export const daysUntil = (iso, now = new Date()) => {
  const day = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return 0;
  return Math.max(0, Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${todayISO(now)}T00:00:00Z`)) / 86400000));
};

// dueCell: the due pill (label and tone) and the line under it.
export const dueCell = (row, now = new Date()) => {
  switch (row.contact_state) {
    case 'due':
      return {
        label: row.days_overdue ? `${row.days_overdue}d overdue` : 'Due today',
        tone: 'negative',
        sub: `was ${formatShortDay(row.next_contact_at)}`,
      };
    case 'never_contacted':
      return { label: 'Due now', tone: 'warning', sub: 'first contact' };
    case 'scheduled':
      return { label: `In ${daysUntil(row.next_contact_at, now)}d`, tone: 'info', sub: formatShortDay(row.next_contact_at) };
    default:
      return { label: 'No follow-up', tone: 'muted', sub: 'not scheduled' };
  }
};

export const isDueRow = (row) => row.contact_state === 'due' || row.contact_state === 'never_contacted';

// nextQueuePlayer: the due row "Work the queue" opens: after the open one (if listed), skipping
// players already contacted today; null when none is left on the page.
export const nextQueuePlayer = (rows, openId = null, now = new Date()) => {
  const at = rows.findIndex((r) => r.user_id === openId);
  const contactedToday = (r) => r.last_contact && daysAgo(r.last_contact.contacted_at, now) === 0;
  return rows.slice(at + 1).find((r) => isDueRow(r) && !contactedToday(r)) || null;
};

// queueRanks: each due row's place in the queue (offset by earlier pages); null for the others.
export const queueRanks = (rows, offset = 0) => rows.reduce(
  (acc, r) => [...acc, isDueRow(r) ? offset + acc.filter((x) => x !== null).length + 1 : null], [],
);

// FOLLOW_UP_PRESETS: days from today for the next contact.
export const FOLLOW_UP_PRESETS = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
];

export const addDays = (ymd, n) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

const TOP_VALUE_NGR_30D = 10000;
const CHURN_RISK_DAYS = 14;

// suggestedFollowUpDays: a quiet player (or none on record, null) gets two weeks, a top-value one
// three days, others a week. Unknown activity (undefined, the profile) goes by value only.
export const suggestedFollowUpDays = (row) => {
  const quiet = row.days_inactive === null || row.days_inactive >= CHURN_RISK_DAYS;
  if (quiet) return 14;
  if ((row.ngr_30d || 0) >= TOP_VALUE_NGR_30D) return 3;
  return 7;
};

export const playerName = (row) => row?.username || row?.email || `Player ${row?.user_id}`;

// savedToast: "Logged Email for Ty — next contact 29 Oct".
export const savedToast = (channel, name, nextContactAt) => `Logged ${channel} for ${name}${
  nextContactAt ? ` — next contact ${formatShortDay(nextContactAt)}` : ' — no follow-up'}`;
